import { Component, OnInit, OnDestroy } from '@angular/core';
import { Params, Router, ActivatedRoute } from '@angular/router';
import { QuestionsService } from 'src/app/services/stat-questions/stat-questions.service';
import { Questions } from 'src/app/models/data/stat-questions.model';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Observable, take } from 'rxjs';
import { StatsAnswer } from 'src/app/models/data/stat-answer.model';
import { LoadingSpinnerComponent } from '../../../shared/loading-spinner/loading-spinner.component';
import { DropdownService } from 'src/app/services/dropdown/dropdown.service';
import { PageService } from 'src/app/services/page/page.service';
import { Page } from 'src/app/models/data/page.model';
import { CommonModule } from '@angular/common';
import { NumberFormatDirective } from 'src/app/directives/number-format.directive';
import { NumberFormatPipe } from 'src/app/pipes/number-format.pipe';
import { NumberFormatter } from 'src/app/tools/number-formatter';
import { max10DigitsValidator } from 'src/app/validators/max-digits.validator';
import { AutoSaveService } from 'src/app/services/auto-save/auto-save.service';
import { CycleService } from 'src/app/services/cycle/cycle.service';

@Component({
  selector: 'app-question-detail',
  templateUrl: './stat-question-detail.component.html',
  styleUrls: ['./stat-question-detail.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LoadingSpinnerComponent, NumberFormatDirective],
})
export class StatQuestionDetailComponent implements OnInit, OnDestroy {
  id!: number;
  questions$!: Observable<Questions[]>;
  questions: Questions[] = [];
  questionsForm!: FormGroup;
  isLoading: boolean = false;
  pageHeader = 'Six-Month Ministry Survey';
  cycleBanner: string = '';
  header!: string;
  error!: string | null;
  instructions =
    ' Please fill out this survey form with the best information available covering the last six months. Estimates are OK. Place answers inside the fields (no text or symbols; whole numbers only).';
  extraInstructions =
    '(A "Church" is a group of followers of Jesus who fellowship with one another; does not refer to a building. Many ministries are planting churches in homes, huts/tents, or under trees.)';

  moreInstructions = `If a question was not applicable to your ministry work in the last six months, please enter "0".`;

  constructor(
    private fb: FormBuilder,
    private questionService: QuestionsService,
    private route: ActivatedRoute,
    private router: Router,
    private dropdownService: DropdownService,
    private pageService: PageService,
    private autoSaveService: AutoSaveService,
    private cycleService: CycleService,
  ) { }

  ngOnInit() {
    this.questionsForm = this.fb.group({
      answers: this.fb.array([])
    });

    // Fetch cycle banner from API
    this.cycleService.getCycleBanner().subscribe({
      next: (banner) => {
        console.log('📋 STAT QUESTION: Cycle banner received:', banner);
        this.cycleBanner = banner;
      },
      error: (error) => {
        console.error('📋 STAT QUESTION: Error getting cycle banner:', error);
        this.cycleBanner = '';
      }
    });

    // Get current route path
    const currentPath = this.router.url.substring(1); // Remove leading slash

    // Find page data from PageService
    this.pageService.pages.pipe(take(1)).subscribe((pages: Page[]) => {
      const currentPage = pages.find(p => p.Route === currentPath);
      if (currentPage) {
        this.header = currentPage.Title;
        this.id = currentPage.PageNumber - 1; // Convert to 0-based index for API

        this.isLoading = true;
        this.questionService.getPage(this.id).subscribe((res) => {
          this.isLoading = false;
        });
        this.questions$ = this.questionService.getSection();

        this.questions$.subscribe(questions => {
          this.buildAnswersForm(questions);
        });
      }
    });
  }

  private buildAnswersForm(questions: Questions[]) {
    this.questions = questions; // Store questions for template access
    const answersArray = this.answersArray;
    answersArray.clear();

    questions.forEach((question, index) => {
      const answerGroup = this.fb.group({
        questionNum: [question.QuestionNum],
        answer: [question.Answer || '', [Validators.required, max10DigitsValidator()]]
      });

      answersArray.push(answerGroup);

      // Initialize auto-save for this field
      const fieldId = `stat-question-${this.id}-${question.QuestionNum}`;
      this.initializeAutoSaveForField(fieldId, question.QuestionNum, answerGroup.get('answer') as FormControl);
    });
  }

  private initializeAutoSaveForField(fieldId: string, questionNum: number, formControl: FormControl) {
    const saveFunction = async (value: any) => {
      // Allow saving even if field is empty (to support clearing answers)
      // Form validation still handles required field validation on submit
      // For Type 1 questions, send -1 for empty values to represent unanswered state
      if (value === null || value === undefined || value === '') {
        return this.questionService.updateSingleAnswer(questionNum, '-1', false);
      }
      const rawValue = NumberFormatter.getRawNumber(value);
      // If getRawNumber returns null, use original value (might be a string that's not a number)
      const finalValue = rawValue !== null ? String(rawValue) : String(value);
      return this.questionService.updateSingleAnswer(questionNum, finalValue, false);
    };

    this.autoSaveService.initializeAutoSave(fieldId, saveFunction, {
      debounceMs: 500,
      validateField: true
    });
  }

  onFieldBlur(index: number) {
    const answerGroup = this.answersArray.at(index);
    const questionNum = answerGroup.get('questionNum')?.value;
    const answer = answerGroup.get('answer')?.value;
    const fieldId = `stat-question-${this.id}-${questionNum}`;

    // Allow saving empty strings, null, or undefined to support clearing answers
    // For Type 1 questions, -1 will be sent to represent unanswered state
    // Convert null/undefined to empty string for consistency
    const valueToSave = answer === null || answer === undefined ? '' : answer;
    this.autoSaveService.triggerSave(fieldId, valueToSave);
  }

  ngOnDestroy() {
    // Clean up auto-save for all fields in this component
    this.questions.forEach(question => {
      const fieldId = `stat-question-${this.id}-${question.QuestionNum}`;
      this.autoSaveService.cleanup(fieldId);
    });
  }

  onForward(): void {
    if (this.questionsForm.invalid) {
      this.error =
        'Please fill in all fields as it applies to your ministry. If a question does not apply, please fill in a 0.';
      return;
    }

    let answerArray: StatsAnswer[] = [];
    const answers = this.answersArray.value;

    answers.forEach((answer: any) => {
      // Ensure we send raw numbers without commas to the API
      const rawValue = NumberFormatter.getRawNumber(answer.answer) || answer.answer;
      answerArray.push(new StatsAnswer(answer.questionNum, String(rawValue)));
    });

    this.questionService.updateAnswers(answerArray);

    // Get next page from database
    this.pageService.pages.pipe(take(1)).subscribe((pages: Page[]) => {
      const sortedPages = pages.sort((a, b) => a.PageNumber - b.PageNumber);
      const currentPageIndex = sortedPages.findIndex(p => p.PageNumber === this.id + 1);

      if (currentPageIndex >= 0 && currentPageIndex < sortedPages.length - 1) {
        const nextPage = sortedPages[currentPageIndex + 1];
        const nextRoute = nextPage.Route.startsWith('/') ? nextPage.Route : `/${nextPage.Route}`;
        this.router.navigate([nextRoute]);
      } else {
        // If this is the last page, navigate to current ethnic groups
        this.router.navigate(['/current-ethnic-groups']);
      }
    });
  }

  onClose() {
    this.error = null;
  }

  get answersArray(): FormArray {
    return this.questionsForm.get('answers') as FormArray;
  }

  get answerGroups(): FormGroup[] {
    return this.answersArray.controls as FormGroup[];
  }
}
